import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider
} from '@tanstack/react-query'
import { ThemeProvider } from 'next-themes'
import { lazy, StrictMode, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation
} from 'react-router'

import './index.css'
import { getConfig } from '@/api/config'
import { ensureServiceWorker } from '@/api/sw'
import { ErrorBoundary } from '@/components/error-boundary'
import { ScrollToTop } from '@/components/scroll-to-top'
import { Toaster } from '@/components/ui/sonner'
import { usePageView, useReturnDetection } from '@/domains/analytics'
import {
  FullPageSpinner,
  GuestRoute,
  ProtectedRoute,
  RequireSuper,
  RequireSupplier,
  RootRedirect,
  useSessionRevokedRedirect,
  useUser
} from '@/domains/auth'
import { RequireBilling, useBillingBootstrap } from '@/domains/billing'
import {
  CommandPalette,
  CommandPaletteProvider,
  useRegisterAppPages
} from '@/domains/command-palette'
import { AddCompanySheetProvider, DirtyFormProvider } from '@/domains/companies'
import { captureInstallPrompt } from '@/domains/install'
import { useMessagingBootstrap } from '@/domains/messaging'
import {
  useNotificationBootstrap,
  useRegisterNotificationsSection
} from '@/domains/notifications'
import {
  initAnalytics,
  initOtel,
  initSentry,
  reportCacheError
} from '@/domains/observability'
import { RequireSupplierSettings } from '@/domains/settings'
import { SetupProvider, useSetupState } from '@/domains/setup'
import { PublicRoute } from '@/domains/supplier-directory'
import { useRegisterThemeSection } from '@/domains/theme'

const LoginPage = lazy(() => import('@/pages/login'))
const SignupPage = lazy(() => import('@/pages/signup'))
const SignupVerifyPage = lazy(() => import('@/pages/signup-verify'))
const SignupFinishPage = lazy(() => import('@/pages/signup-finish'))
const ForgotPasswordPage = lazy(() => import('@/pages/forgot-password'))
const ResetPasswordPage = lazy(() => import('@/pages/reset-password'))
const AcceptInvitePage = lazy(() => import('@/pages/accept-invite'))
const OnboardingPage = lazy(() => import('@/pages/onboarding'))
const OnboardingCompanyDetailsPage = lazy(
  () => import('@/pages/onboarding-company-details')
)
const OnboardingCompanyContactsPage = lazy(
  () => import('@/pages/onboarding-company-contacts')
)
const OnboardingWorkEmailPage = lazy(
  () => import('@/pages/onboarding-work-email')
)
const OnboardingSupplierDueDiligencePage = lazy(
  () => import('@/pages/onboarding-supplier-due-diligence')
)
const OnboardingSupplierQualityAssessmentPage = lazy(
  () => import('@/pages/onboarding-supplier-quality-assessment')
)
const OnboardingSupplierDocumentsPage = lazy(
  () => import('@/pages/onboarding-supplier-documents')
)
const OnboardingSupplierPoliciesPage = lazy(
  () => import('@/pages/onboarding-supplier-policies')
)
const OnboardingSupplierWorkforcePage = lazy(
  () => import('@/pages/onboarding-supplier-workforce')
)
const OnboardingSupplierCapabilitiesPage = lazy(
  () => import('@/pages/onboarding-supplier-capabilities')
)
const OnboardingSupplierIndustriesPage = lazy(
  () => import('@/pages/onboarding-supplier-industries')
)
const OnboardingSupplierSalesPage = lazy(
  () => import('@/pages/onboarding-supplier-sales')
)
const OnboardingSupplierLocationsPage = lazy(
  () => import('@/pages/onboarding-supplier-locations')
)
const OnboardingSupplierEquipmentPage = lazy(
  () => import('@/pages/onboarding-supplier-equipment')
)
const OnboardingSupplierMeasurementPage = lazy(
  () => import('@/pages/onboarding-supplier-measurement')
)
const OnboardingSupplierQualityPage = lazy(
  () => import('@/pages/onboarding-supplier-quality')
)
const OnboardingSupplierPlanPage = lazy(
  () => import('@/pages/onboarding-supplier-plan')
)
const NotFoundPage = lazy(() => import('@/pages/not-found'))
const MessagesPage = lazy(() => import('@/pages/messages'))
const MessagesNewPage = lazy(() => import('@/pages/messages-new'))
const MessagesDetailPage = lazy(() => import('@/pages/messages-detail'))
const NotificationsPage = lazy(() => import('@/pages/notifications'))
const InsightsPage = lazy(() => import('@/pages/insights'))
const PlatformMetricsPage = lazy(() => import('@/pages/platform-metrics'))
const UsersPage = lazy(() => import('@/pages/users'))
const UsersDetailPage = lazy(() => import('@/pages/users-detail'))
const IntelligencePage = lazy(() => import('@/pages/intelligence'))
const InvoicesPage = lazy(() => import('@/pages/invoices'))
const InvoiceCreatePage = lazy(() => import('@/pages/invoice-create'))
const InvoiceDetailPage = lazy(() => import('@/pages/invoice-detail'))
const SupplierNetworkPage = lazy(() => import('@/pages/suppliers'))
const SuppliersDetailPage = lazy(() => import('@/pages/suppliers-detail'))
const PublicSupplierNetworkPage = lazy(() => import('@/pages/supplier-network'))
const PublicSupplierProfilePage = lazy(
  () => import('@/pages/supplier-network-profile')
)
const RfiPage = lazy(() => import('@/pages/rfi'))
const RfiCreatePage = lazy(() => import('@/pages/rfi-create'))
const RfiDetailPage = lazy(() => import('@/pages/rfi-detail'))
const EmployeesPage = lazy(() => import('@/pages/employees'))
const SettingsAccountPage = lazy(() => import('@/pages/settings-account'))
const SettingsAppearancePage = lazy(() => import('@/pages/settings-appearance'))
const BillingPage = lazy(() => import('@/pages/billing'))
const SettingsCompanyDetailsPage = lazy(
  () => import('@/pages/settings-company-details')
)
const SettingsCompanyContactsPage = lazy(
  () => import('@/pages/settings-company-contacts')
)
const SettingsSupplierPoliciesPage = lazy(
  () => import('@/pages/settings-supplier-policies')
)
const SettingsSupplierWorkforcePage = lazy(
  () => import('@/pages/settings-supplier-workforce')
)
const SettingsSupplierCapabilitiesPage = lazy(
  () => import('@/pages/settings-supplier-capabilities')
)
const SettingsSupplierIndustriesPage = lazy(
  () => import('@/pages/settings-supplier-industries')
)
const SettingsSupplierSalesPage = lazy(
  () => import('@/pages/settings-supplier-sales')
)
const SettingsSupplierLocationsPage = lazy(
  () => import('@/pages/settings-supplier-locations')
)
const SettingsSupplierEquipmentPage = lazy(
  () => import('@/pages/settings-supplier-equipment')
)
const DocumentsPage = lazy(() => import('@/pages/documents'))
const CertificatesPage = lazy(() => import('@/pages/certificates'))
const CertificateReviewPage = lazy(() => import('@/pages/certificate-review'))
const DueDiligencePage = lazy(() => import('@/pages/due-diligence'))
const DueDiligenceDetailPage = lazy(
  () => import('@/pages/due-diligence-detail')
)
const CompaniesPage = lazy(() => import('@/pages/companies'))
const RfisPage = lazy(() => import('@/pages/rfis'))
const SuperBillingPage = lazy(() => import('@/pages/super-billing'))
const SuperResetDemoDataPage = lazy(
  () => import('@/pages/super-reset-demo-data')
)
const SuperBillingCompanyPage = lazy(
  () => import('@/pages/super-billing-company')
)
const SuperEmailTemplatesPage = lazy(
  () => import('@/pages/super-email-templates')
)

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: false
    }
  },
  /** capture query + mutation failures at the cache boundary, where the
   * queryKey/mutationKey is in hand and the stack is intact — not by
   * intercepting console.error under zone.js, where the originating frames are
   * already gone. reportCacheError filters cancellations + expected 4xx. */
  queryCache: new QueryCache({
    onError: (error, query) =>
      reportCacheError(error, { queryKey: query.queryKey })
  }),
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) =>
      reportCacheError(error, {
        mutationKey: mutation.options.mutationKey ?? null
      })
  })
})

/**
 * TrackPageview wraps every route, including pre-auth (login, signup), so
 * usePageView fires on all navigations. lets us see signup-funnel dropoff.
 */
function TrackPageview() {
  usePageView()
  useReturnDetection()
  return <Outlet />
}

/**
 * RegisterUser adds the company-scoped super-props (user_id, email, etc.)
 * only after the user is identified.
 */
function RegisterUser() {
  return (
    <CommandPaletteProvider>
      <SetupProvider>
        {/* tracks "any form mid-edit?" so the company switcher can
         * prompt before re-scoping. forms register via
         * useRegisterDirtyForm, nothing breaks if a form forgets to,
         * the registry just reports nothing dirty. */}
        <DirtyFormProvider>
          <AddCompanySheetProvider>
            <SessionBootstrap />
            <NotificationsBootstrap />
            <ThemeBootstrap />
            <PagesBootstrap />
            <MessagingBootstrap />
            <BillingBootstrap />
            <OnboardingGate />
          </AddCompanySheetProvider>
        </DirtyFormProvider>
      </SetupProvider>
      <CommandPalette />
    </CommandPaletteProvider>
  )
}

function SessionBootstrap() {
  useSessionRevokedRedirect()
  return null
}

function NotificationsBootstrap() {
  useNotificationBootstrap()
  useRegisterNotificationsSection()
  return null
}

function ThemeBootstrap() {
  useRegisterThemeSection()
  return null
}

function PagesBootstrap() {
  useRegisterAppPages()
  return null
}

function MessagingBootstrap() {
  useMessagingBootstrap()
  return null
}

function BillingBootstrap() {
  useBillingBootstrap()
  return null
}

/**
 * hard-enforces the onboarding redirect: any user whose active company is
 * still in onboarding gets bounced to /onboarding from every protected
 * route except the onboarding routes themselves. lives downstream of
 * SetupProvider so useSetupState resolves. a super acting as the system
 * company and the /onboarding subtree pass through unconditionally.
 *
 * /messages and /notifications are also allowed during onboarding so
 * super can nudge the company ("upload your insurance cert", etc.) and
 * the company sees those nudges land while still working through setup.
 */
function OnboardingGate() {
  const { user } = useUser()
  const { setupState } = useSetupState()
  const location = useLocation()

  if (!setupState) return <FullPageSpinner />
  /**
   * super tools live in the system company, so a super acting there gets the
   * full app. a super acting as a regular company they own is just its owner
   * and follows the same onboarding gate as everyone else.
   */
  if (user?.isSuper && setupState.scope === 'super') return <Outlet />

  /**
   * account-level settings stay reachable mid-onboarding so a user can manage
   * how they sign in (set a password, connect google, change email, theme)
   * before finishing setup. company + supplier settings are the onboarding
   * steps themselves and stay gated. getSettingsNav hides those groups to
   * match, so keep this allowlist in sync with the account group there.
   * /settings/account is the retired url and still redirects here.
   */
  const path = location.pathname
  const isAccountSettings =
    path === '/settings' ||
    path.startsWith('/settings/account') ||
    path.startsWith('/settings/appearance')
  const isAllowedDuringOnboarding =
    path.startsWith('/onboarding') ||
    path.startsWith('/messages') ||
    path.startsWith('/notifications') ||
    isAccountSettings
  if (setupState.isOnboarding && !isAllowedDuringOnboarding) {
    return <Navigate to='/onboarding' replace />
  }
  return <Outlet />
}

async function initObservability() {
  try {
    const config = await getConfig()
    initOtel({ appEnv: config.appEnv, appVersion: config.appVersion })
    initSentry({
      dsn: config.sentryDsn,
      appEnv: config.appEnv,
      appVersion: config.appVersion
    })
    initAnalytics({ appEnv: config.appEnv })
  } catch (err) {
    console.warn('initObservability failed:', err)
  }

  /* before the first render, the install event fires early and is lost unless
   * something is already listening. */
  captureInstallPrompt()
  void ensureServiceWorker()

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <ThemeProvider
        attribute='class'
        defaultTheme='light'
        enableSystem={false}
        storageKey='tandemone-ui-theme'
      >
        <QueryClientProvider client={queryClient}>
          <Toaster
            position='top-right'
            richColors
            closeButton
            mobileOffset={{ top: 64 }}
          />
          <BrowserRouter>
            <ScrollToTop />
            <ErrorBoundary>
              <Suspense fallback={<FullPageSpinner />}>
                <Routes>
                  <Route element={<TrackPageview />}>
                    <Route path='/' element={<RootRedirect />} />
                    <Route element={<GuestRoute />}>
                      <Route path='/login' element={<LoginPage />} />
                      <Route
                        path='/forgot-password'
                        element={<ForgotPasswordPage />}
                      />
                      <Route
                        path='/reset-password'
                        element={<ResetPasswordPage />}
                      />
                    </Route>
                    {/* /signup + /signup/finish both live outside GuestRoute
                     * because the auth transition crosses the guest/protected
                     * boundary. each page reads session state and redirects
                     * accordingly. dedicated finish URL keeps analytics for
                     * the post-auth surface distinct from initial signup. */}
                    <Route path='/signup' element={<SignupPage />} />
                    <Route path='/verify' element={<SignupVerifyPage />} />
                    <Route
                      path='/signup/finish'
                      element={<SignupFinishPage />}
                    />
                    {/* invite accept is reachable logged-out (sign-up/in
                     * round-trip) and logged-in (accept), so it sits outside
                     * both GuestRoute and ProtectedRoute, like /signup. */}
                    <Route
                      path='/invite/accept'
                      element={<AcceptInvitePage />}
                    />
                    <Route element={<PublicRoute />}>
                      <Route
                        path='/supplier-network'
                        element={<PublicSupplierNetworkPage />}
                      />
                      <Route
                        path='/supplier-network/:slug'
                        element={<PublicSupplierProfilePage />}
                      />
                    </Route>
                    <Route element={<ProtectedRoute />}>
                      <Route element={<RegisterUser />}>
                        <Route
                          path='/intelligence'
                          element={<IntelligencePage />}
                        />
                        {/* metals and rates became tabs. */}
                        <Route
                          path='/intelligence/metals'
                          element={<Navigate to='/intelligence' replace />}
                        />
                        <Route
                          path='/intelligence/rates'
                          element={
                            <Navigate to='/intelligence?tab=rates' replace />
                          }
                        />
                        <Route element={<RequireSupplier />}>
                          <Route path='/invoices' element={<InvoicesPage />} />
                          {/* now a tab, like /rfi/received below. */}
                          <Route
                            path='/invoices/received'
                            element={
                              <Navigate to='/invoices?tab=received' replace />
                            }
                          />
                          <Route
                            path='/invoices/create'
                            element={<InvoiceCreatePage />}
                          />
                          <Route
                            path='/invoices/:id'
                            element={<InvoiceDetailPage />}
                          />
                        </Route>
                        <Route element={<RequireSupplier redirectTo='/rfi' />}>
                          <Route path='/insights' element={<InsightsPage />} />
                        </Route>
                        <Route
                          path='/suppliers'
                          element={<SupplierNetworkPage />}
                        />
                        {/* now tabs. without these they would open a supplier
                            with that slug. */}
                        <Route
                          path='/suppliers/favorites'
                          element={
                            <Navigate to='/suppliers?tab=favorites' replace />
                          }
                        />
                        <Route
                          path='/suppliers/founding'
                          element={
                            <Navigate to='/suppliers?tab=founding' replace />
                          }
                        />
                        <Route
                          path='/suppliers/verified'
                          element={
                            <Navigate to='/suppliers?tab=verified' replace />
                          }
                        />
                        <Route
                          path='/suppliers/:slug'
                          element={<SuppliersDetailPage />}
                        />
                        <Route path='/rfi' element={<RfiPage />} />
                        {/* the old received page, now a tab. without this it
                            would match /rfi/:id. caddy redirects full loads. */}
                        <Route
                          path='/rfi/received'
                          element={<Navigate to='/rfi?tab=received' replace />}
                        />
                        <Route path='/rfi/create' element={<RfiCreatePage />} />
                        <Route path='/rfi/:id' element={<RfiDetailPage />} />
                        <Route element={<RequireSupplier />}>
                          <Route
                            path='/employees'
                            element={<EmployeesPage />}
                          />
                          <Route
                            path='/documents'
                            element={<DocumentsPage />}
                          />
                          <Route
                            path='/certificates'
                            element={<CertificatesPage />}
                          />
                        </Route>
                        <Route path='/messages' element={<MessagesPage />} />
                        <Route
                          path='/messages/:conversationId'
                          element={<MessagesDetailPage />}
                        />
                        <Route
                          path='/notifications'
                          element={<NotificationsPage />}
                        />
                        <Route
                          path='/onboarding'
                          element={<OnboardingPage />}
                        />
                        <Route
                          path='/onboarding/company/details'
                          element={<OnboardingCompanyDetailsPage />}
                        />
                        <Route
                          path='/onboarding/company/contacts'
                          element={<OnboardingCompanyContactsPage />}
                        />
                        <Route
                          path='/onboarding/work-email'
                          element={<OnboardingWorkEmailPage />}
                        />
                        <Route
                          path='/onboarding/supplier/due-diligence'
                          element={<OnboardingSupplierDueDiligencePage />}
                        />
                        <Route
                          path='/onboarding/supplier/quality-assessment'
                          element={<OnboardingSupplierQualityAssessmentPage />}
                        />
                        <Route
                          path='/onboarding/supplier/documents'
                          element={<OnboardingSupplierDocumentsPage />}
                        />
                        <Route
                          path='/onboarding/supplier/plan'
                          element={<OnboardingSupplierPlanPage />}
                        />
                        <Route
                          path='/onboarding/supplier/policies'
                          element={<OnboardingSupplierPoliciesPage />}
                        />
                        <Route
                          path='/onboarding/supplier/workforce'
                          element={<OnboardingSupplierWorkforcePage />}
                        />
                        <Route
                          path='/onboarding/supplier/capabilities'
                          element={<OnboardingSupplierCapabilitiesPage />}
                        />
                        <Route
                          path='/onboarding/supplier/operations'
                          element={
                            <Navigate
                              to='/onboarding/supplier/capabilities'
                              replace
                            />
                          }
                        />
                        <Route
                          path='/onboarding/supplier/industries'
                          element={<OnboardingSupplierIndustriesPage />}
                        />
                        <Route
                          path='/onboarding/supplier/sales'
                          element={<OnboardingSupplierSalesPage />}
                        />
                        <Route
                          path='/onboarding/supplier/locations'
                          element={<OnboardingSupplierLocationsPage />}
                        />
                        <Route
                          path='/onboarding/supplier/equipment'
                          element={<OnboardingSupplierEquipmentPage />}
                        />
                        <Route
                          path='/onboarding/supplier/measurement'
                          element={<OnboardingSupplierMeasurementPage />}
                        />
                        <Route
                          path='/onboarding/supplier/quality/:section'
                          element={<OnboardingSupplierQualityPage />}
                        />
                        {/* billing is its own top-level section, not a
                            settings page: it is money, not a preference. */}
                        <Route element={<RequireBilling />}>
                          <Route element={<RequireSupplier />}>
                            <Route path='/billing' element={<BillingPage />} />
                          </Route>
                        </Route>
                        {/* account settings are the settings page: the bare
                            path renders rather than redirecting. */}
                        <Route
                          path='/settings'
                          element={<SettingsAccountPage />}
                        />
                        {/* old urls, kept so links already in inboxes and
                            stripe redirects still land. */}
                        <Route
                          path='/settings/billing'
                          element={<Navigate to='/billing' replace />}
                        />
                        <Route
                          path='/billing/payment-method'
                          element={
                            <Navigate
                              to='/billing?tab=payment-method'
                              replace
                            />
                          }
                        />
                        <Route
                          path='/billing/payment-history'
                          element={
                            <Navigate
                              to='/billing?tab=payment-history'
                              replace
                            />
                          }
                        />
                        <Route
                          path='/settings/billing/payments'
                          element={
                            <Navigate
                              to='/billing?tab=payment-history'
                              replace
                            />
                          }
                        />
                        <Route
                          path='/billing/method'
                          element={
                            <Navigate
                              to='/billing?tab=payment-method'
                              replace
                            />
                          }
                        />
                        <Route
                          path='/billing/payments'
                          element={
                            <Navigate
                              to='/billing?tab=payment-history'
                              replace
                            />
                          }
                        />
                        <Route
                          path='/settings/account'
                          element={<Navigate to='/settings' replace />}
                        />
                        {/* operations became capabilities, which is what the
                            step was always about. kept for saved links. */}
                        <Route
                          path='/settings/supplier/operations'
                          element={
                            <Navigate
                              to='/settings/supplier/capabilities'
                              replace
                            />
                          }
                        />
                        <Route
                          path='/settings/appearance'
                          element={<SettingsAppearancePage />}
                        />
                        <Route
                          path='/settings/company-details'
                          element={<SettingsCompanyDetailsPage />}
                        />
                        <Route
                          path='/settings/company-contacts'
                          element={<SettingsCompanyContactsPage />}
                        />
                        <Route element={<RequireSupplierSettings />}>
                          <Route
                            path='/settings/supplier/policies'
                            element={<SettingsSupplierPoliciesPage />}
                          />
                          <Route
                            path='/settings/supplier/workforce'
                            element={<SettingsSupplierWorkforcePage />}
                          />
                          <Route
                            path='/settings/supplier/capabilities'
                            element={<SettingsSupplierCapabilitiesPage />}
                          />
                          <Route
                            path='/settings/supplier/industries'
                            element={<SettingsSupplierIndustriesPage />}
                          />
                          <Route
                            path='/settings/supplier/sales-history'
                            element={<SettingsSupplierSalesPage />}
                          />
                          <Route
                            path='/settings/supplier/manufacturing-locations'
                            element={<SettingsSupplierLocationsPage />}
                          />
                          <Route
                            path='/settings/supplier/capital-equipment'
                            element={<SettingsSupplierEquipmentPage />}
                          />
                        </Route>
                        <Route element={<RequireSuper />}>
                          <Route path='/super'>
                            {/* the shared surfaces, mirrored so staff page
                                views split by path. same pages either side. */}
                            <Route path='messages' element={<MessagesPage />} />
                            <Route
                              path='messages/new'
                              element={<MessagesNewPage />}
                            />
                            <Route
                              path='messages/:conversationId'
                              element={<MessagesDetailPage />}
                            />
                            <Route
                              path='notifications'
                              element={<NotificationsPage />}
                            />
                            <Route
                              path='platform-metrics'
                              element={<PlatformMetricsPage />}
                            />
                            <Route
                              path='suppliers'
                              element={<CompaniesPage />}
                            />
                            <Route path='buyers' element={<CompaniesPage />} />
                            <Route
                              path='distributors'
                              element={<CompaniesPage />}
                            />
                            <Route path='rfis' element={<RfisPage />} />
                            <Route element={<RequireBilling />}>
                              <Route
                                path='billing'
                                element={<SuperBillingPage />}
                              />
                              <Route
                                path='billing/:slug'
                                element={<SuperBillingCompanyPage />}
                              />
                            </Route>
                            <Route
                              path='rfis/:id'
                              element={<RfiDetailPage />}
                            />
                            <Route
                              path='due-diligence'
                              element={<DueDiligencePage />}
                            />
                            {/* now a tab. without this it would open a
                                company with the slug "hidden". */}
                            <Route
                              path='due-diligence/hidden'
                              element={
                                <Navigate
                                  to='/super/due-diligence?tab=hidden'
                                  replace
                                />
                              }
                            />
                            <Route
                              path='due-diligence/:slug'
                              element={<DueDiligenceDetailPage />}
                            />
                            <Route
                              path='email-templates'
                              element={<SuperEmailTemplatesPage />}
                            />
                            <Route
                              path='email-templates/:group'
                              element={<SuperEmailTemplatesPage />}
                            />
                            <Route
                              path='email-templates/:group/:slug'
                              element={<SuperEmailTemplatesPage />}
                            />
                            <Route path='users' element={<UsersPage />} />
                            <Route
                              path='users/:id'
                              element={<UsersDetailPage />}
                            />
                            <Route
                              path='certificate-review'
                              element={<CertificateReviewPage />}
                            />
                            <Route
                              path='reset-demo-data'
                              element={<SuperResetDemoDataPage />}
                            />
                          </Route>
                        </Route>
                      </Route>
                    </Route>
                    <Route path='*' element={<NotFoundPage />} />
                  </Route>
                </Routes>
              </Suspense>
            </ErrorBoundary>
          </BrowserRouter>
        </QueryClientProvider>
      </ThemeProvider>
    </StrictMode>
  )
}

void initObservability()
